import type { ReactNode } from 'react'

const label = { fill: 'var(--c-star-3)', fontSize: 9, fontFamily: 'var(--font-app-mono, monospace)', letterSpacing: 1 } as const
const serif = { fill: 'var(--c-star-2)', fontSize: 12, fontFamily: 'var(--font-app-heading, serif)', fontStyle: 'italic' } as const
const hair = { stroke: 'var(--c-hair-2)', strokeWidth: 1, fill: 'none' } as const
const brass = { stroke: 'var(--c-brass)', strokeWidth: 1, fill: 'none' } as const

const Star = ({ x, y, r = 2.2, bright = false }: { x: number, y: number, r?: number, bright?: boolean }): ReactNode => (
    <g>
        {bright && <circle cx={x} cy={y} r={r * 2.6} fill='var(--c-brass)' opacity={0.15} />}
        <circle cx={x} cy={y} r={r} fill={bright ? 'var(--c-brass)' : 'var(--c-star)'} />
    </g>
)

/** The Plough's pointer stars lead to Polaris, which sits as high as your latitude. */
export const NorthStarDiagram = (): ReactNode => {
    const handle: [number, number][] = [[60, 222], [95, 206], [128, 202], [160, 206]]
    const bowl: [number, number][] = [[160, 206], [166, 240], [226, 242], [232, 198], [160, 206]]

    return (
        <svg viewBox='0 0 400 300' className='block h-full w-full' role='img' aria-label='Diagram: follow the two pointer stars at the end of the Plough upward about five times their separation to reach Polaris, which stands 51 degrees above the northern horizon from Exmoor.'>
            <line x1={20} y1={276} x2={380} y2={276} {...hair} />
            <text x={200} y={292} textAnchor='middle' {...label}>
                NORTH HORIZON
            </text>
            <line x1={76} y1={276} x2={254} y2={56} stroke='var(--c-hair)' strokeDasharray='1 4' />
            <path d='M 116 276 A 40 40 0 0 0 101.2 244.9' {...brass} />
            <text x={122} y={262} {...label} fill='var(--c-brass)'>
                51°
            </text>
            <polyline points={handle.map(point => point.join(',')).join(' ')} {...hair} />
            <polyline points={bowl.map(point => point.join(',')).join(' ')} {...hair} />
            <line x1={226} y1={242} x2={253} y2={64} {...brass} strokeDasharray='4 4' />
            {[...handle, ...bowl.slice(1, 4)].map(([x, y]) => (
                <Star key={`${x}-${y}`} x={x} y={y} r={2.4} />
            ))}
            <Star x={254} y={56} r={3.2} bright />
            <text x={266} y={52} {...serif}>
                Polaris
            </text>
            <text x={238} y={256} {...label}>
                MERAK
            </text>
            <text x={242} y={198} {...label}>
                DUBHE
            </text>
            <text x={264} y={130} {...label} fill='var(--c-brass)'>
                ABOUT FIVE TIMES THE GAP
            </text>
            <text x={40} y={190} {...serif}>
                The Plough
            </text>
        </svg>
    )
}

/** Phone against the eyepiece: line up the lenses, then turn the exposure down. */
export const MoonPhoneDiagram = (): ReactNode => (
    <svg viewBox='0 0 400 300' className='block h-full w-full' role='img' aria-label='Diagram: hold the phone camera lens centred on the telescope eyepiece, a few millimetres away, then lower the exposure until craters along the terminator show.'>
        <rect x={40} y={120} width={150} height={60} rx={4} {...hair} />
        <rect x={190} y={134} width={34} height={32} rx={3} {...brass} />
        <line x1={224} y1={150} x2={252} y2={150} stroke='var(--c-brass)' strokeDasharray='2 3' />
        <rect x={252} y={70} width={70} height={150} rx={12} {...hair} />
        <circle cx={262} cy={150} r={6} {...brass} />
        <text x={115} y={110} textAnchor='middle' {...label}>
            TELESCOPE
        </text>
        <text x={207} y={196} textAnchor='middle' {...label}>
            EYEPIECE
        </text>
        <text x={287} y={240} textAnchor='middle' {...label}>
            PHONE
        </text>
        <circle cx={330} cy={60} r={22} {...hair} />
        <path d='M 330 38 A 22 22 0 0 1 330 82 A 10 22 0 0 0 330 38 Z' fill='var(--c-star)' opacity={0.85} />
        <text x={360} y={64} {...serif}>
            the terminator
        </text>
        <g transform='translate(48 230)'>
            <text x={0} y={0} {...label}>
                1 · 1X LENS
            </text>
            <text x={0} y={16} {...label}>
                2 · TAP TO FOCUS
            </text>
            <text x={0} y={32} {...label} fill='var(--c-brass)'>
                3 · EXPOSURE DOWN
            </text>
        </g>
    </svg>
)

/** How long light takes, on a scale where every step is a thousand times further. */
export const LightYearDiagram = (): ReactNode => {
    const stops = [
        { x: 40, name: 'The Moon', time: '1.3 seconds' },
        { x: 110, name: 'The Sun', time: '8 minutes 20 seconds' },
        { x: 180, name: 'Neptune', time: '4 hours' },
        { x: 250, name: 'Proxima Centauri', time: '4.2 years' },
        { x: 320, name: 'Orion Nebula', time: '1,344 years' },
        { x: 370, name: 'Andromeda', time: '2.5 million years' }
    ]

    return (
        <svg viewBox='0 0 400 300' className='block h-full w-full' role='img' aria-label='Diagram: light takes 1.3 seconds from the Moon, 8 minutes from the Sun, 4 hours from Neptune, 4.2 years from the nearest star, and 2.5 million years from the Andromeda Galaxy.'>
            <line x1={20} y1={160} x2={385} y2={160} {...hair} />
            {Array.from({ length: 37 }, (_, index) => (
                <line key={index} x1={20 + index * 10} y1={160} x2={20 + index * 10} y2={index % 5 === 0 ? 150 : 155} stroke='var(--c-hair-2)' />
            ))}
            <circle cx={20} cy={160} r={3} fill='var(--c-brass)' />
            <text x={20} y={182} {...label}>
                YOU
            </text>
            {stops.map((stop, index) => (
                <g key={stop.name}>
                    <circle cx={stop.x} cy={160} r={2.5} fill='var(--c-star)' />
                    <line x1={stop.x} y1={160} x2={stop.x} y2={index % 2 === 0 ? 112 : 206} stroke='var(--c-hair)' />
                    <text x={stop.x > 340 ? stop.x + 6 : stop.x} y={index % 2 === 0 ? 96 : 226} textAnchor={stop.x > 340 ? 'end' : 'middle'} {...serif}>
                        {stop.name}
                    </text>
                    <text x={stop.x > 340 ? stop.x + 6 : stop.x} y={index % 2 === 0 ? 108 : 240} textAnchor={stop.x > 340 ? 'end' : 'middle'} {...label} fill='var(--c-brass)'>
                        {stop.time.toUpperCase()}
                    </text>
                </g>
            ))}
            <text x={20} y={280} {...label}>
                EACH MAJOR TICK IS A THOUSAND TIMES FURTHER THAN THE LAST
            </text>
        </svg>
    )
}

/** Three telescopes and the size of the light they gather. */
export const TelescopeDiagram = (): ReactNode => (
    <svg viewBox='0 0 400 300' className='block h-full w-full' role='img' aria-label='Diagram: a small refractor, a tabletop reflector and a large Dobsonian, with circles showing how much more light a wider opening gathers.'>
        <g transform='translate(30 60)'>
            <rect x={0} y={60} width={90} height={16} rx={3} {...hair} transform='rotate(-30 45 68)' />
            <line x1={44} y1={84} x2={30} y2={170} {...hair} />
            <line x1={44} y1={84} x2={58} y2={170} {...hair} />
            <circle cx={45} cy={210} r={8} {...brass} />
            <text x={45} y={238} textAnchor='middle' {...label}>
                80 MM
            </text>
        </g>
        <g transform='translate(150 60)'>
            <rect x={10} y={70} width={70} height={40} rx={4} {...hair} transform='rotate(-25 45 90)' />
            <rect x={20} y={130} width={50} height={36} {...hair} />
            <circle cx={45} cy={210} r={13} {...brass} />
            <text x={45} y={238} textAnchor='middle' {...label}>
                130 MM
            </text>
        </g>
        <g transform='translate(270 30)'>
            <rect x={20} y={20} width={44} height={150} rx={4} {...hair} transform='rotate(-14 42 95)' />
            <path d='M 10 180 L 74 180 L 66 196 L 18 196 Z' {...hair} />
            <circle cx={45} cy={240} r={26} {...brass} />
            <text x={45} y={286} textAnchor='middle' {...label}>
                250 MM
            </text>
        </g>
        <text x={200} y={22} textAnchor='middle' {...serif}>
            Aperture, not magnification, decides what you see
        </text>
    </svg>
)

/** Hold the chart over your head and the east and west fall into place. */
export const StarChartDiagram = (): ReactNode => (
    <svg viewBox='0 0 400 300' className='block h-full w-full' role='img' aria-label='Diagram: a circular sky chart with north at the top, east on the left and west on the right, because it is meant to be held above your head while you face north.'>
        <circle cx={200} cy={150} r={110} {...brass} />
        <circle cx={200} cy={150} r={74} {...hair} strokeDasharray='2 5' />
        <circle cx={200} cy={150} r={38} {...hair} strokeDasharray='2 5' />
        <line x1={200} y1={40} x2={200} y2={260} {...hair} />
        <line x1={90} y1={150} x2={310} y2={150} {...hair} />
        <text x={200} y={30} textAnchor='middle' {...label} fill='var(--c-star)'>
            N
        </text>
        <text x={200} y={280} textAnchor='middle' {...label} fill='var(--c-star)'>
            S
        </text>
        <text x={74} y={154} textAnchor='middle' {...label} fill='var(--c-brass)'>
            E
        </text>
        <text x={326} y={154} textAnchor='middle' {...label} fill='var(--c-brass)'>
            W
        </text>
        <circle cx={200} cy={150} r={2.5} fill='var(--c-star)' />
        <text x={206} y={144} {...label}>
            ZENITH
        </text>
        {[[150, 110], [172, 98], [196, 104], [214, 120], [236, 116]].map(([x, y]) => (
            <Star key={`${x}-${y}`} x={x} y={y} r={2} />
        ))}
        <polyline points='150,110 172,98 196,104 214,120 236,116' {...hair} stroke='var(--c-brass)' strokeOpacity={0.6} />
        <text x={30} y={60} {...serif}>
            East is on the left
        </text>
        <text x={30} y={76} {...serif}>
            because you look up at it
        </text>
    </svg>
)

/** How the eye wakes up in the dark: cones first, then the slow rods. */
export const DarkAdaptationDiagram = (): ReactNode => {
    const x = (minutes: number) => 50 + minutes * 10.5
    const y = (sensitivity: number) => 250 - sensitivity * 2

    const curve: string[] = []

    for (let minute = 0; minute <= 32; minute += 0.5) {
        const cones = 26 * (1 - Math.exp(-minute / 2.2))
        const rods = minute < 7 ? 0 : 74 * (1 - Math.exp(-(minute - 7) / 7))

        curve.push(`${x(minute).toFixed(1)},${y(Math.max(cones, cones + rods)).toFixed(1)}`)
    }

    return (
        <svg viewBox='0 0 400 300' className='block h-full w-full' role='img' aria-label='Diagram: the eye becomes more sensitive in two stages, a quick rise in the first seven minutes and a slower climb that continues for thirty minutes.'>
            <line x1={50} y1={40} x2={50} y2={250} {...hair} />
            <line x1={50} y1={250} x2={386} y2={250} {...hair} />
            {[0, 5, 10, 15, 20, 25, 30].map(minute => (
                <g key={minute}>
                    <line x1={x(minute)} y1={250} x2={x(minute)} y2={255} stroke='var(--c-hair-2)' />
                    <text x={x(minute)} y={268} textAnchor='middle' {...label}>
                        {minute}
                    </text>
                </g>
            ))}
            <text x={218} y={288} textAnchor='middle' {...label}>
                MINUTES IN THE DARK
            </text>
            <text x={40} y={34} {...label}>
                SENSITIVITY
            </text>
            <polyline points={curve.join(' ')} {...brass} strokeWidth={1.4} />
            <line x1={x(7)} y1={60} x2={x(7)} y2={250} stroke='var(--c-hair)' strokeDasharray='2 4' />
            <text x={x(7) + 6} y={200} {...serif}>
                cones settle
            </text>
            <text x={x(17)} y={92} {...serif}>
                rods take over
            </text>
            <text x={x(24)} y={232} {...label} fill='var(--c-signal)'>
                ONE WHITE LIGHT RESETS THIS
            </text>
        </svg>
    )
}

export const DIAGRAMS = {
    'north-star': NorthStarDiagram,
    'moon-phone': MoonPhoneDiagram,
    'light-year': LightYearDiagram,
    telescope: TelescopeDiagram,
    'star-chart': StarChartDiagram,
    'dark-adaptation': DarkAdaptationDiagram
} as const

export type DiagramKey = keyof typeof DIAGRAMS
